/** Plain-English audit sentences for back-office modules. No PINs, passwords, or tokens. */

type LineLike = { name?: string; productName?: string; description?: string; quantity?: number };

function clip(value: string): string {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  return trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed;
}

function asText(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'string') return clip(value);
  return '';
}

export function formatAuditQtyList(items: LineLike[] | undefined): string {
  if (!items?.length) return '';
  const shown = items.slice(0, 8).map((item) => {
    const name = clip(String(item.productName || item.description || item.name || 'Item'));
    const qty = Number(item.quantity ?? 1);
    const count = Number.isFinite(qty) && qty > 0 ? qty : 1;
    return `${name} × ${count}`;
  });
  const rest = items.length - shown.length;
  return rest > 0 ? `${shown.join(', ')}, +${rest} more` : shown.join(', ');
}

export function labeledChanges(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
  fields: Array<[string, string]>,
): string[] {
  if (!after) return [];
  const lines: string[] = [];
  for (const [key, label] of fields) {
    if (!(key in after)) continue;
    if (/password|secret|token|hash/i.test(key)) continue;
    const prev = before?.[key];
    const next = after[key];
    if (key === 'pin') {
      if (typeof next === 'string' && next && next !== prev) lines.push('PIN changed');
      continue;
    }
    if (asText(prev) === asText(next)) continue;
    const nextText = asText(next);
    const prevText = asText(prev);
    if (!nextText && !prevText) continue;
    lines.push(prevText ? `${label} ${prevText} → ${nextText}` : `${label} ${nextText}`);
  }
  return lines;
}

function withChanges(title: string, changes: string[]): string {
  return changes.length ? `${title} · ${changes.join(' · ')}` : `${title} saved`;
}

const CUSTOMER_FIELDS: Array<[string, string]> = [
  ['name', 'Name'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['loyaltyPoints', 'Points'],
  ['discountPercentage', 'Discount %'],
  ['notes', 'Notes'],
  ['isResident', 'Resident'],
  ['studentVerified', 'Student checked'],
];

const STAFF_FIELDS: Array<[string, string]> = [
  ['name', 'Name'],
  ['role', 'Role'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['isAdmin', 'Admin'],
  ['includeInRoster', 'On roster'],
  ['pin', 'PIN'],
];

const ASSET_FIELDS: Array<[string, string]> = [
  ['name', 'Name'],
  ['category', 'Category'],
  ['location', 'Location'],
  ['quantity', 'Qty'],
  ['status', 'Status'],
  ['condition', 'Condition'],
  ['assetTag', 'Tag'],
];

const SUPPLIER_FIELDS: Array<[string, string]> = [
  ['name', 'Name'],
  ['contactPerson', 'Contact'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['paymentTerms', 'Terms'],
];

const BILLING_CLIENT_FIELDS: Array<[string, string]> = [
  ['name', 'Name'],
  ['contactPerson', 'Contact'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['paymentTerms', 'Terms'],
];

export function customerAuditSummary(kind: 'added' | 'saved' | 'removed', name: string, before?: Record<string, unknown>, after?: Record<string, unknown>): string {
  if (kind === 'added') return `Customer added · ${name}`;
  if (kind === 'removed') return `Customer removed · ${name}`;
  return withChanges(name, labeledChanges(before, after, CUSTOMER_FIELDS));
}

export function staffAuditSummary(kind: 'added' | 'saved' | 'removed', name: string, role?: string, before?: Record<string, unknown>, after?: Record<string, unknown>): string {
  if (kind === 'added') return role ? `Staff added · ${name} · ${role}` : `Staff added · ${name}`;
  if (kind === 'removed') return `Staff removed · ${name}`;
  return withChanges(name, labeledChanges(before, after, STAFF_FIELDS));
}

export function assetAuditSummary(kind: 'added' | 'saved' | 'removed', name: string, quantity?: number, before?: Record<string, unknown>, after?: Record<string, unknown>): string {
  if (kind === 'added') return `Asset added · ${name} · qty ${quantity ?? 1}`;
  if (kind === 'removed') return `Asset removed · ${name}`;
  return withChanges(name, labeledChanges(before, after, ASSET_FIELDS));
}

export function supplierAuditSummary(kind: 'added' | 'saved' | 'removed', name: string, before?: Record<string, unknown>, after?: Record<string, unknown>): string {
  if (kind === 'added') return `Supplier added · ${name}`;
  if (kind === 'removed') return `Supplier removed · ${name}`;
  return withChanges(name, labeledChanges(before, after, SUPPLIER_FIELDS));
}

export function billingClientAuditSummary(kind: 'added' | 'saved' | 'removed', name: string, before?: Record<string, unknown>, after?: Record<string, unknown>): string {
  if (kind === 'added') return `Billing client added · ${name}`;
  if (kind === 'removed') return `Billing client removed · ${name}`;
  return withChanges(name, labeledChanges(before, after, BILLING_CLIENT_FIELDS));
}

export function purchaseOrderAuditSummary(
  id: string,
  before: { status?: string; items?: LineLike[]; notes?: string } | undefined,
  after: { status?: string; items?: LineLike[]; notes?: string },
  created: boolean,
): string {
  const lines = formatAuditQtyList(after.items);
  if (created) return [`Purchase order ${id} created`, after.status, lines].filter(Boolean).join(' · ');
  const parts: string[] = [];
  if (before?.status && after.status && before.status !== after.status) parts.push(`status ${before.status} → ${after.status}`);
  if (after.items && formatAuditQtyList(before?.items) !== lines) parts.push(lines || 'lines cleared');
  if (after.notes !== undefined && (before?.notes || '') !== (after.notes || '')) parts.push('notes updated');
  return parts.length ? `Purchase order ${id} · ${parts.join(' · ')}` : `Purchase order ${id} saved`;
}

export function invoiceAuditSummary(
  kind: 'Supplier invoice' | 'Client invoice',
  invoiceNumber: string,
  before: { paymentStatus?: string; items?: LineLike[] } | undefined,
  after: { paymentStatus?: string; items?: LineLike[] },
  created: boolean,
): string {
  const lines = formatAuditQtyList(after.items);
  if (created) return [`${kind} ${invoiceNumber} added`, after.paymentStatus, lines].filter(Boolean).join(' · ');
  const parts: string[] = [];
  if (before?.paymentStatus && after.paymentStatus && before.paymentStatus !== after.paymentStatus) {
    parts.push(`payment ${before.paymentStatus} → ${after.paymentStatus}`);
  }
  if (after.items && formatAuditQtyList(before?.items) !== lines) parts.push(lines || 'lines cleared');
  return parts.length ? `${kind} ${invoiceNumber} · ${parts.join(' · ')}` : `${kind} ${invoiceNumber} saved`;
}

const SETTING_AREAS: Array<[string, string]> = [
  ['kitchenSLA', 'Kitchen alerts'],
  ['purchasingInvoiceDefaults', 'Purchasing defaults'],
  ['cateringAlerts', 'Catering alerts'],
  ['receiptPrinters', 'Printers'],
  ['buffetPackages', 'Buffet packages'],
  ['fireMode', 'Fire mode'],
  ['overrideReasons', 'Manager reasons'],
  ['flashPnl', 'Flash P&L'],
];

function businessDetailsChanged(before: unknown, after: unknown): boolean {
  const left = before && typeof before === 'object' ? { ...(before as Record<string, unknown>) } : {};
  const right = after && typeof after === 'object' ? { ...(after as Record<string, unknown>) } : {};
  delete left.onlineOrderingEnabled;
  delete right.onlineOrderingEnabled;
  return JSON.stringify(left) !== JSON.stringify(right);
}

export function settingsAuditSummary(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  onlineSummary: string | null,
): string | null {
  const areas = SETTING_AREAS
    .filter(([key]) => JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null))
    .map(([, label]) => label);
  if (businessDetailsChanged(before.businessInfo, after.businessInfo)) areas.unshift('Business details');
  if (JSON.stringify(before.uberDoordashPriceIncreasePercent ?? null) !== JSON.stringify(after.uberDoordashPriceIncreasePercent ?? null)) {
    areas.push('Uber and DoorDash markup');
  }
  const parts = [onlineSummary, areas.length ? areas.join(', ') : ''].filter(Boolean) as string[];
  if (parts.length) return parts.join(' · ');
  return JSON.stringify(before) === JSON.stringify(after) ? null : 'Settings saved';
}
